// XlsParser.ts - High-level XLS (BIFF8) workbook parser
//
// Reads a .xls file (CFB container → Workbook stream → BIFF8 records) and
// produces the same data structures consumed by WorkbookManager / SheetRenderer
// so the rest of the application is format-agnostic.

import { parseCfb, type CfbContainer } from './CfbReader';
import {
  BIFF,
  iterateBiffRecords,
  decodeRkValue,
  decodeBiffString,
  decodeBiffShortString,
  type BiffRecord,
} from './BiffReader';
import type { SheetInfo, StyleResult, FontInfo, CellXf, FillInfo, BorderDef } from '../types';

// ---- Public result types ----

/** A single cell parsed from the XLS file. */
export interface XlsCellData {
  row: number;
  col: number;
  value: any;
  formula: string | null;
  xfIndex: number;
}

/** A parsed sheet from the XLS file. */
export interface XlsSheet {
  name: string;
  hidden: boolean;
  /** Offset of the BOF record for this sheet's substream in the workbook stream. */
  bofOffset: number;
  cells: XlsCellData[];
  mergedRanges: Array<{ startRow: number; startCol: number; endRow: number; endCol: number }>;
}

/** Full parse result of an XLS workbook. */
export interface XlsParseResult {
  sheets: XlsSheet[];
  sharedStrings: string[];
  styles: StyleResult;
  dateMode1904: boolean;
}

// ---- Internal helpers ----

/** Standard indexed color palette (first 8 + 8 system colors). */
const DEFAULT_PALETTE: string[] = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
  '#800000', '#008000', '#000080', '#808000', '#800080', '#008080', '#C0C0C0', '#808080',
  '#9999FF', '#993366', '#FFFFCC', '#CCFFFF', '#660066', '#FF8080', '#0066CC', '#CCCCFF',
  '#000080', '#FF00FF', '#FFFF00', '#00FFFF', '#800080', '#800000', '#008080', '#0000FF',
  '#00CCFF', '#CCFFFF', '#CCFFCC', '#FFFF99', '#99CCFF', '#FF99CC', '#CC99FF', '#FFCC99',
  '#3366FF', '#33CCCC', '#99CC00', '#FFCC00', '#FF9900', '#FF6600', '#666699', '#969696',
  '#003366', '#339966', '#003300', '#333300', '#993300', '#993366', '#333399', '#333333',
];

function indexedColor(idx: number, palette: string[]): string | null {
  if (idx >= 8 && idx < 8 + palette.length) return palette[idx - 8];
  if (idx < palette.length) return palette[idx];
  return null;
}

/**
 * Parse an entire XLS file from an ArrayBuffer.
 */
export function parseXls(data: ArrayBuffer): XlsParseResult {
  const cfb = parseCfb(data);

  // The workbook stream can be named "Workbook" (BIFF8) or "Book" (BIFF5/7)
  let wbStream = cfb.getStream('Workbook');
  if (!wbStream) wbStream = cfb.getStream('Book');
  if (!wbStream) {
    throw new Error('No Workbook stream found in XLS file');
  }

  return parseWorkbookStream(wbStream);
}

/**
 * Parse the BIFF8 Workbook stream into structured data.
 */
function parseWorkbookStream(buf: Uint8Array): XlsParseResult {
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);

  // ---- Globals substream data ----
  const boundSheets: Array<{ name: string; hidden: boolean; offset: number }> = [];
  const sst: string[] = [];
  const fonts: FontInfo[] = [];
  const xfs: CellXf[] = [];
  const palette = [...DEFAULT_PALETTE];
  let dateMode1904 = false;
  let isEncrypted = false;

  // ---- First pass: read the Globals substream ----
  // The Globals substream is from the first BOF to its matching EOF.

  let inGlobals = false;
  let globalsDone = false;

  // We iterate through records, tracking byte position for BOUNDSHEET offsets.
  // iterateBiffRecords yields coalesced records; we process globals first.
  let bytePos = 0;
  const allRecords: Array<{ type: number; data: Uint8Array; offset: number }> = [];

  // Manual iteration to track offsets (since iterateBiffRecords uses a generator)
  {
    let pos = 0;
    let pending: { type: number; data: Uint8Array; offset: number } | null = null;

    while (pos + 4 <= buf.length) {
      const recOffset = pos;
      const type = view.getUint16(pos, true);
      const len = view.getUint16(pos + 2, true);
      pos += 4;
      if (pos + len > buf.length) break;
      const data = buf.subarray(pos, pos + len);
      pos += len;

      if (type === BIFF.CONTINUE && pending) {
        const merged: Uint8Array = new Uint8Array(pending.data.length + data.length);
        merged.set(pending.data, 0);
        merged.set(data, pending.data.length);
        pending = { type: pending.type, data: merged, offset: pending.offset };
      } else {
        if (pending) allRecords.push(pending);
        pending = { type, data, offset: recOffset };
      }
    }
    if (pending) allRecords.push(pending);
  }

  let globalsEndIdx = 0;

  for (let ri = 0; ri < allRecords.length; ri++) {
    const rec = allRecords[ri];

    if (rec.type === BIFF.BOF && !inGlobals && !globalsDone) {
      inGlobals = true;
      continue;
    }
    if (rec.type === BIFF.EOF && inGlobals) {
      inGlobals = false;
      globalsDone = true;
      globalsEndIdx = ri + 1;
      continue;
    }

    if (!inGlobals) continue;

    switch (rec.type) {
      case BIFF.FILEPASS:
        isEncrypted = true;
        break;

      case BIFF.DATEMODE:
        if (rec.data.length >= 2) {
          dateMode1904 = new DataView(rec.data.buffer, rec.data.byteOffset, rec.data.byteLength).getUint16(0, true) === 1;
        }
        break;

      case BIFF.BOUNDSHEET:
        parseBoundSheet(rec.data, boundSheets);
        break;

      case BIFF.SST:
        parseSst(rec.data, sst);
        break;

      case BIFF.FONT:
        parseFont(rec.data, fonts);
        break;

      case BIFF.XF:
        parseXf(rec.data, xfs);
        break;

      case BIFF.PALETTE:
        parsePalette(rec.data, palette);
        break;
    }
  }

  if (isEncrypted) {
    throw new Error('Encrypted XLS files are not supported');
  }

  // ---- Second pass: read each sheet substream ----
  const sheets: XlsSheet[] = [];

  for (const bs of boundSheets) {
    const sheet = parseSheetSubstream(allRecords, globalsEndIdx, bs, sst);
    sheets.push(sheet);
  }

  // ---- Build StyleResult ----
  const styles: StyleResult = {
    fonts,
    cellXfs: xfs,
    cellStyleXfs: [],
    themeColors: {},
    dxfs: [],
    fills: [],
    borders: [],
  };

  return { sheets, sharedStrings: sst, styles, dateMode1904 };
}

// ---- Record parsers ----

function parseBoundSheet(
  data: Uint8Array,
  out: Array<{ name: string; hidden: boolean; offset: number }>,
): void {
  if (data.length < 8) return;
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const bofOffset = dv.getUint32(0, true);
  const visibility = data[4]; // 0=visible, 1=hidden, 2=very hidden
  // data[5] = sheet type (0=worksheet, others ignored)
  const { text } = decodeBiffShortString(data, 6);
  out.push({ name: text, hidden: visibility !== 0, offset: bofOffset });
}

function parseSst(data: Uint8Array, out: string[]): void {
  if (data.length < 8) return;
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  // Bytes 0-3: total string references
  // Bytes 4-7: unique string count
  const uniqueCount = dv.getInt32(4, true);
  let pos = 8;

  for (let i = 0; i < uniqueCount && pos < data.length; i++) {
    try {
      const { text, bytesConsumed } = decodeBiffString(data, pos);
      out.push(text);
      pos += bytesConsumed;
    } catch {
      // Malformed string: push empty and skip rest
      out.push('');
      break;
    }
  }
}

function parseFont(data: Uint8Array, out: FontInfo[]): void {
  if (data.length < 14) return;
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const height = dv.getUint16(0, true); // in 1/20 pt
  const grbit = dv.getUint16(2, true);
  const colorIdx = dv.getUint16(4, true);
  const weight = dv.getUint16(6, true);

  const font: FontInfo = {
    pt: height / 20,
    bold: weight >= 700 || (grbit & 0x01) !== 0,
    italic: (grbit & 0x02) !== 0,
    underline: (grbit & 0x04) !== 0,
    strike: (grbit & 0x08) !== 0,
  };

  // Color
  if (colorIdx !== 0x7fff) {
    const c = indexedColor(colorIdx, DEFAULT_PALETTE);
    if (c) font.rgb = c.replace('#', 'FF');
  }

  // Name (ShortXLUnicodeString starting at offset 14)
  if (data.length > 14) {
    try {
      const { text } = decodeBiffShortString(data, 14);
      font.name = text;
    } catch { /* ignore */ }
  }

  out.push(font);
}

function parseXf(data: Uint8Array, out: CellXf[]): void {
  if (data.length < 20) return;
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const fontId = dv.getUint16(0, true);
  // const numFmtId = dv.getUint16(2, true);
  const fillPatternArea = dv.getUint32(8, true);
  const borderArea = dv.getUint32(12, true);

  out.push({
    fontId: String(fontId),
    applyFont: '1',
    fillId: null,
    applyFill: null,
    borderId: null,
    applyBorder: null,
  });
}

function parsePalette(data: Uint8Array, palette: string[]): void {
  if (data.length < 2) return;
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const count = dv.getUint16(0, true);
  for (let i = 0; i < count && (2 + i * 4 + 4) <= data.length; i++) {
    const r = data[2 + i * 4];
    const g = data[2 + i * 4 + 1];
    const b = data[2 + i * 4 + 2];
    palette[i] = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
  }
}

// ---- Sheet substream parser ----

function parseSheetSubstream(
  allRecords: Array<{ type: number; data: Uint8Array; offset: number }>,
  globalsEndIdx: number,
  bs: { name: string; hidden: boolean; offset: number },
  sst: string[],
): XlsSheet {
  const cells: XlsCellData[] = [];
  const mergedRanges: XlsSheet['mergedRanges'] = [];

  // Find the BOF record for this sheet by its offset
  let startIdx = -1;
  for (let i = globalsEndIdx; i < allRecords.length; i++) {
    if (allRecords[i].type === BIFF.BOF && allRecords[i].offset === bs.offset) {
      startIdx = i + 1;
      break;
    }
  }

  if (startIdx < 0) {
    return { name: bs.name, hidden: bs.hidden, bofOffset: bs.offset, cells, mergedRanges };
  }

  // Track the last FORMULA record so the subsequent STRING record can be associated
  let lastFormulaCell: { row: number; col: number; xfIndex: number } | null = null;

  for (let i = startIdx; i < allRecords.length; i++) {
    const rec = allRecords[i];
    if (rec.type === BIFF.EOF) break;

    const dv = new DataView(rec.data.buffer, rec.data.byteOffset, rec.data.byteLength);

    switch (rec.type) {
      case BIFF.LABELSST: {
        if (rec.data.length < 10) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);
        const sstIdx = dv.getUint32(6, true);
        cells.push({
          row: row + 1, // BIFF is 0-based, our model is 1-based
          col: col + 1,
          value: sstIdx < sst.length ? sst[sstIdx] : '',
          formula: null,
          xfIndex: xfIdx,
        });
        lastFormulaCell = null;
        break;
      }

      case BIFF.LABEL: {
        if (rec.data.length < 8) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);
        try {
          const { text } = decodeBiffString(rec.data, 6);
          cells.push({ row: row + 1, col: col + 1, value: text, formula: null, xfIndex: xfIdx });
        } catch { /* skip malformed */ }
        lastFormulaCell = null;
        break;
      }

      case BIFF.RSTRING: {
        if (rec.data.length < 8) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);
        try {
          const { text } = decodeBiffString(rec.data, 6);
          cells.push({ row: row + 1, col: col + 1, value: text, formula: null, xfIndex: xfIdx });
        } catch { /* skip malformed */ }
        lastFormulaCell = null;
        break;
      }

      case BIFF.NUMBER: {
        if (rec.data.length < 14) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);
        const num = dv.getFloat64(6, true);
        cells.push({ row: row + 1, col: col + 1, value: num, formula: null, xfIndex: xfIdx });
        lastFormulaCell = null;
        break;
      }

      case BIFF.RK: {
        if (rec.data.length < 10) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);
        const rk = dv.getInt32(6, true);
        cells.push({ row: row + 1, col: col + 1, value: decodeRkValue(rk), formula: null, xfIndex: xfIdx });
        lastFormulaCell = null;
        break;
      }

      case BIFF.MULRK: {
        if (rec.data.length < 6) break;
        const row = dv.getUint16(0, true);
        const firstCol = dv.getUint16(2, true);
        // Each RK entry: [xfIndex: uint16][rk: int32] = 6 bytes
        // Last 2 bytes = lastCol
        const nEntries = (rec.data.length - 6) / 6;
        for (let j = 0; j < nEntries; j++) {
          const off = 4 + j * 6;
          if (off + 6 > rec.data.length) break;
          const xfIdx = dv.getUint16(off, true);
          const rk = dv.getInt32(off + 2, true);
          cells.push({ row: row + 1, col: firstCol + j + 1, value: decodeRkValue(rk), formula: null, xfIndex: xfIdx });
        }
        lastFormulaCell = null;
        break;
      }

      case BIFF.BLANK: {
        if (rec.data.length < 6) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);
        cells.push({ row: row + 1, col: col + 1, value: '', formula: null, xfIndex: xfIdx });
        lastFormulaCell = null;
        break;
      }

      case BIFF.MULBLANK: {
        if (rec.data.length < 6) break;
        const row = dv.getUint16(0, true);
        const firstCol = dv.getUint16(2, true);
        const lastCol = dv.getUint16(rec.data.length - 2, true);
        for (let c = firstCol; c <= lastCol; c++) {
          const off = 4 + (c - firstCol) * 2;
          const xfIdx = off + 2 <= rec.data.length - 2 ? dv.getUint16(off, true) : 0;
          cells.push({ row: row + 1, col: c + 1, value: '', formula: null, xfIndex: xfIdx });
        }
        lastFormulaCell = null;
        break;
      }

      case BIFF.BOOLERR: {
        if (rec.data.length < 8) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);
        const bVal = rec.data[6];
        const isErr = rec.data[7];
        if (isErr) {
          // Error value
          const errMap: Record<number, string> = {
            0x00: '#NULL!', 0x07: '#DIV/0!', 0x0f: '#VALUE!',
            0x17: '#REF!', 0x1d: '#NAME?', 0x24: '#NUM!', 0x2a: '#N/A',
          };
          cells.push({ row: row + 1, col: col + 1, value: errMap[bVal] || '#ERROR!', formula: null, xfIndex: xfIdx });
        } else {
          cells.push({ row: row + 1, col: col + 1, value: bVal === 1, formula: null, xfIndex: xfIdx });
        }
        lastFormulaCell = null;
        break;
      }

      case BIFF.FORMULA: {
        if (rec.data.length < 20) break;
        const row = dv.getUint16(0, true);
        const col = dv.getUint16(2, true);
        const xfIdx = dv.getUint16(4, true);

        // Result: 8 bytes at offset 6
        // If byte 6 == 0x00 and byte 12-13 == 0xFFFF → string result (STRING record follows)
        // If byte 6 == 0x01 and byte 12-13 == 0xFFFF → boolean
        // If byte 6 == 0x02 and byte 12-13 == 0xFFFF → error
        // Otherwise → IEEE double
        const byte12 = dv.getUint16(12, true);
        let value: any;

        if (byte12 === 0xffff) {
          const special = rec.data[6];
          if (special === 0x00) {
            // String — value will come in the next STRING record
            value = '';
            lastFormulaCell = { row: row + 1, col: col + 1, xfIndex: xfIdx };
          } else if (special === 0x01) {
            value = rec.data[8] === 1;
            lastFormulaCell = null;
          } else if (special === 0x02) {
            const errCode = rec.data[8];
            const errMap: Record<number, string> = {
              0x00: '#NULL!', 0x07: '#DIV/0!', 0x0f: '#VALUE!',
              0x17: '#REF!', 0x1d: '#NAME?', 0x24: '#NUM!', 0x2a: '#N/A',
            };
            value = errMap[errCode] || '#ERROR!';
            lastFormulaCell = null;
          } else {
            value = '';
            lastFormulaCell = null;
          }
        } else {
          value = dv.getFloat64(6, true);
          lastFormulaCell = null;
        }

        // We store the formula's cached value. We don't decode the BIFF formula
        // bytecode (ptg tokens) here — the formula engine re-evaluates from A1
        // notation. We note formula presence for cells that need re-evaluation.
        // The formula field is set to null; the recalc engine will use the cached
        // value from the XLS file.
        cells.push({ row: row + 1, col: col + 1, value, formula: null, xfIndex: xfIdx });
        break;
      }

      case BIFF.STRING: {
        // Only valid after a FORMULA record with a string result
        if (lastFormulaCell && rec.data.length >= 3) {
          try {
            const { text } = decodeBiffString(rec.data, 0);
            // Update the last formula cell's value
            const lastCell = cells.find(
              (c) => c.row === lastFormulaCell!.row && c.col === lastFormulaCell!.col,
            );
            if (lastCell) lastCell.value = text;
          } catch { /* ignore */ }
        }
        lastFormulaCell = null;
        break;
      }

      case BIFF.MERGEDCELLS: {
        if (rec.data.length < 2) break;
        const count = dv.getUint16(0, true);
        for (let j = 0; j < count; j++) {
          const off = 2 + j * 8;
          if (off + 8 > rec.data.length) break;
          mergedRanges.push({
            startRow: dv.getUint16(off, true) + 1,
            endRow: dv.getUint16(off + 2, true) + 1,
            startCol: dv.getUint16(off + 4, true) + 1,
            endCol: dv.getUint16(off + 6, true) + 1,
          });
        }
        break;
      }
    }
  }

  return { name: bs.name, hidden: bs.hidden, bofOffset: bs.offset, cells, mergedRanges };
}
